import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { EventExtra } from '@cocoonspace/sdk-js/types/eventExtra'
import type { EventExtraChoiceItem } from '@cocoonspace/sdk-js/types/eventExtraChoiceItem'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Plural, useLingui } from '@lingui/react/macro'

import { ExtraMenuPicker } from './extra-menu-picker'

export const ExtraMenusChoices = ({
	event,
	extra,
	readOnly,
	onMenuChange,
}: {
	event?: Pick<Event, 'extras_allowed_until'>
	extra?: Pick<EventExtra, 'quantity' | 'choices'>
	readOnly?: boolean
	onMenuChange: (extraMenu: EventExtraChoiceItem) => void
}) => {
	const { t } = useLingui()

	const extraChoicesCount =
		extra?.choices?.reduce((acc, menu) => acc + menu.quantity, 0) || 0

	const tooManyMenus = extraChoicesCount > (extra?.quantity || 0)
	const missingMenus = extra?.quantity && extraChoicesCount < extra?.quantity

	const totalMenus = extraChoicesCount
	const qty = extra?.quantity
	const date = dateTimeUtils(event?.extras_allowed_until).customFormat(
		'dateMonthDay',
	)

	return (
		<div
			className='flex flex-col gap-4 p-4'
			data-testid='extra-menu-list'
		>
			<div>
				{!readOnly && (
					<p className='text-sm font-semibold'>
						{t`Choisissez votre menu (${totalMenus}/${qty}) : *`}
					</p>
				)}

				{missingMenus && event?.extras_allowed_until && (
					<p
						data-testid='missing-menus-msg'
						className='text-sm'
					>
						<Plural
							value={extra.quantity - extraChoicesCount}
							one={`🍽 Il vous reste # menu à choisir avant le ${date}.`}
							other={`🍽 Il vous reste # menus à choisir avant le ${date}.`}
						/>
					</p>
				)}
			</div>

			<div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
				{extra?.choices?.map((menu) => (
					<ExtraMenuPicker
						className='col-span-1 border'
						key={menu.id}
						max={extraChoicesCount < extra?.quantity ? extra?.quantity : 0}
						menu={menu}
						min={0}
						onQtyChange={(quantity) => onMenuChange({ ...menu, quantity })}
						readOnly={readOnly}
					/>
				))}
			</div>

			{tooManyMenus && (
				<p
					className='text-sm'
					data-testid='too-many-menus-msg'
				>
					{t`Le nombre de menus choisis est supérieur au nombre de menus disponibles.`}
				</p>
			)}

			<p className='text-xs'>
				{t`*Pour garantir votre choix auprès de notre prestataire, veuillez sélectionner vos menus 4 jours ouvrés avant votre évènement. Passé ce délai, un menu par défaut sera sélectionné.`}
			</p>
		</div>
	)
}
